import type { TFunction } from "i18next"
import { en } from "@/i18n/en"
import type { Schemas } from "./api"
import { asString, humanize, isRecord } from "./utils"

export type Layer = "tenant" | "registry"

/** One admitted artifact, as served by GET /v1/defs, with the layer it belongs to. */
export interface Artifact {
  readonly artifactId: string
  readonly kind: string
  readonly version: string
  readonly namespace: string | undefined
  readonly name: string | undefined
  readonly lifecycle: string | undefined
  readonly payload: Record<string, unknown>
  /** The envelope exactly as served; candidate saves send these bytes back untouched. */
  readonly raw: Record<string, unknown>
  readonly layer: Layer
}

export interface DefinitionSet {
  readonly version: number
  readonly receiptSha256: string
  readonly registry: readonly string[]
  readonly artifacts: readonly Artifact[]
  readonly diagnosticsCount: number
  readonly kinds: readonly KindSummary[]
  /** Served entries that are not artifact envelopes; shown, never silently dropped. */
  readonly unreadable: number
  /** The editor layout sidecar (#3230): geometry kept beside, never inside, the definitions. */
  readonly layout: Record<string, unknown> | null
}

export interface KindSummary {
  readonly kind: string
  readonly label: string
  readonly total: number
  readonly tenant: number
}

const KIND_ORDER = ["entity", "action", "rule", "mapping", "source_schema", "connector", "policy", "widget"]
const KIND_LABELS = {
  entity: "definitions.kind.entity",
  action: "definitions.kind.action",
  rule: "definitions.kind.rule",
  mapping: "definitions.kind.mapping",
  source_schema: "definitions.kind.sourceSchema",
  connector: "definitions.kind.connector",
  policy: "definitions.kind.policy",
  widget: "definitions.kind.widget",
} as const

export function kindLabel(kind: string, t?: TFunction): string {
  const key = KIND_LABELS[kind as keyof typeof KIND_LABELS]
  return key ? (t ? t(key) : en[key]) : humanize(kind)
}

function countDiagnostics(diagnostics: unknown): number {
  if (Array.isArray(diagnostics)) return diagnostics.length
  if (isRecord(diagnostics)) {
    for (const key of ["diagnostics", "findings", "items"]) {
      const list = diagnostics[key]
      if (Array.isArray(list)) return list.length
    }
  }
  return 0
}

/** `base` holds the registry-base artifact ids the cell reports in `baseArtifactIds`. */
export function toArtifact(raw: unknown, base: ReadonlySet<string>): Artifact | null {
  if (!isRecord(raw)) return null
  const artifactId = asString(raw.artifactId)
  const kind = asString(raw.kind)
  const version = asString(raw.version)
  if (!artifactId || !kind || !version) return null
  return {
    artifactId,
    kind,
    version,
    namespace: asString(raw.namespace),
    name: asString(raw.name),
    lifecycle: asString(raw.lifecycle),
    payload: isRecord(raw.payload) ? raw.payload : {},
    raw,
    layer: base.has(artifactId) ? "registry" : "tenant",
  }
}

export function toDefinitionSet(response: Schemas["DefsResponse"]): DefinitionSet {
  // The cell names the read-only registry base explicitly; a save must exclude exactly these.
  const base = new Set(response.baseArtifactIds)
  const artifacts = (response.artifacts as unknown[]).flatMap((raw) => {
    const artifact = toArtifact(raw, base)
    return artifact ? [artifact] : []
  })
  const byKind = new Map<string, { total: number; tenant: number }>()
  for (const a of artifacts) {
    const entry = byKind.get(a.kind) ?? { total: 0, tenant: 0 }
    entry.total += 1
    if (a.layer === "tenant") entry.tenant += 1
    byKind.set(a.kind, entry)
  }
  const rank = (kind: string) => {
    const i = KIND_ORDER.indexOf(kind)
    return i === -1 ? KIND_ORDER.length : i
  }
  const kinds = [...byKind.entries()]
    .map(([kind, counts]) => ({ kind, label: kindLabel(kind), ...counts }))
    .sort((a, b) => rank(a.kind) - rank(b.kind) || a.kind.localeCompare(b.kind))
  return {
    unreadable: (response.artifacts as unknown[]).length - artifacts.length,
    version: response.version,
    receiptSha256: response.receiptSha256,
    registry: response.registry,
    artifacts,
    diagnosticsCount: countDiagnostics(response.diagnostics),
    kinds,
    layout: isRecord(response.layout) ? (response.layout as unknown as Record<string, unknown>) : null,
  }
}

/** The candidate layer a save sends back: every tenant artifact, byte-for-byte as served. */
export function candidateLayer(set: DefinitionSet): Record<string, unknown>[] {
  return set.artifacts.filter((a) => a.layer === "tenant").map((a) => a.raw)
}

/** A display title: the object type for entities, the declared name otherwise. */
export function artifactTitle(a: Artifact): string {
  if (a.kind === "entity") {
    const def = entityDef(a)
    if (def) return def.name
  }
  return a.name ?? a.artifactId
}

export type EntityForm = "objectType" | "interface" | "extension" | "catalog"

export interface EntityDef {
  readonly form: EntityForm
  readonly name: string
  readonly description: string | undefined
  readonly body: Record<string, unknown>
}

export function entityDef(a: Artifact): EntityDef | null {
  const p = a.payload
  if (isRecord(p.objectType)) return { form: "objectType", name: asString(p.objectType.name) ?? a.artifactId, description: asString(p.objectType.description), body: p.objectType }
  if (isRecord(p.interface)) return { form: "interface", name: asString(p.interface.name) ?? a.artifactId, description: asString(p.interface.description), body: p.interface }
  if (isRecord(p.objectTypeExtension)) {
    const ext = p.objectTypeExtension
    return { form: "extension", name: `${asString(ext.target) ?? "?"} + ${asString(ext.namespace) ?? a.artifactId}`, description: asString(ext.description), body: ext }
  }
  if (typeof p.objectKind === "string" && isRecord(p.fields)) {
    // EntityIR v1: `objectKind` plus a `fields` map, read as properties in key order.
    const properties = Object.entries(p.fields).map(([id, field]) => ({ id, type: isRecord(field) ? { kind: asString(field.type) ?? asString(field.kind) ?? "?" } : { kind: "?" } }))
    return { form: "objectType", name: p.objectKind, description: asString(p.description), body: { properties } }
  }
  if (Array.isArray(p.propertyTypes)) return { form: "catalog", name: a.name ?? a.artifactId, description: undefined, body: { propertyTypes: p.propertyTypes } }
  return null
}

/** Artifacts whose canonical JSON mentions this artifact's id or object type name. */
export function referencesTo(target: Artifact, all: readonly Artifact[]): Artifact[] {
  const needles = new Set([target.artifactId])
  const def = target.kind === "entity" ? entityDef(target) : null
  if (def) needles.add(def.name)
  const quoted = [...needles].map((n) => JSON.stringify(n))
  return all.filter((a) => {
    if (a === target) return false
    const text = JSON.stringify(a.payload)
    return quoted.some((q) => text.includes(q))
  })
}
